import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';

import { GroupEntity } from '@backstage/catalog-model';
import {
  InfoCard,
  InfoCardVariants,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';

import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import AccountBoxIcon from '@material-ui/icons/AccountBox';
import FilterListIcon from '@material-ui/icons/FilterList';

import { ApptioGroupInfoEntity } from 'plugin-apptio-catalog-common';
import { accountmapRouteRef, awsDashboardApiRef } from 'plugin-aws-dashboard';
import { Account } from 'plugin-aws-dashboard-common';
import { AwsIcon } from '../../custom-icons';

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    clickable: {
      cursor: 'pointer',
    },
  }),
);

const CardTitle = ({
  title,
  onClick,
}: {
  title: string;
  onClick: () => void;
}) => {
  const classes = useStyles();

  return (
    <Box
      className={classes.clickable}
      display="flex"
      alignItems="center"
      onClick={onClick}
    >
      <AwsIcon />
      <Box ml={1}>{title}&nbsp;</Box>
      <FilterListIcon />
    </Box>
  );
};

type Props = {
  variant?: InfoCardVariants;
};

export const EntityGroupAwsAccountsCard = (props: Props) => {
  const { variant } = props;
  const classes = useStyles();

  const { entity: group } = useEntity<GroupEntity>();

  const navigate = useNavigate();
  const accountmapRoute = useRouteRef(accountmapRouteRef);

  // With null, assert it definitely does not exist in the catalog
  const [groupInfo, setGroupInfo] = useState<ApptioGroupInfoEntity | null>();

  const awsDashboardApi = useApi(awsDashboardApiRef);
  const catalogApi = useApi(catalogApiRef);

  useEffect(() => {
    (async () => {
      const entity = (await catalogApi.getEntityByRef({
        kind: 'GroupInfo',
        name: group.metadata.name, // 1:1
        namespace: group.metadata.namespace || 'default',
      })) as ApptioGroupInfoEntity | undefined;
      setGroupInfo(entity ?? null);
    })();
  }, []);

  const getAccountsForGroup = () => {
    const accountIds = groupInfo?.spec.awsAccounts ?? [];
    const accounts = accountIds.map(id => awsDashboardApi.getAccount(id));
    return Promise.all(accounts);
  };

  var {
    value: accounts,
    loading,
    error,
  } = useAsync(getAccountsForGroup, [groupInfo]);

  if (groupInfo === undefined || loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  } else if (!accounts) {
    accounts = [];
  }

  const goToAccountDetail = (accounts: Account | Account[]) => {
    if (!Array.isArray(accounts)) {
      accounts = [accounts];
    }

    const search = accounts.map(account => account.id).join(',');

    const params = new URLSearchParams({ search });
    navigate({
      pathname: accountmapRoute(),
      search: `?${params.toString()}`, // search == "" is okay
    });
  };

  return (
    <InfoCard
      title={
        <CardTitle
          title={`AWS Accounts (${accounts.length})`}
          onClick={() => goToAccountDetail(accounts!)}
        />
      }
      variant={variant}
    >
      <List>
        {accounts.map(account => {
          return (
            <ListItem
              className={classes.clickable}
              onClick={() => goToAccountDetail(account)}
            >
              <ListItemIcon>
                <AccountBoxIcon />
              </ListItemIcon>
              <ListItemText primary={account.name} secondary={account.id} />
            </ListItem>
          );
        })}
      </List>
    </InfoCard>
  );
};
